import { StyledDailyBonus } from '../style';
import { ActionButton } from '../../ActionButton';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PopupSingleProduct from '../PopupSingleProduct';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { motion } from 'framer-motion';
import { Box, Modal } from '@mui/material';
import { createGradientOrSolid, formattingNumber } from 'utils';
import { PostPurchaseProps } from '../types';
import '../style.scss';
import { EFontWeights } from 'utils/enums';

const MODAL_EXIT_TIME = 300;
const PRODUCT_EXIT_TIME = 400;

const PostPurchasePopup = forwardRef(
  (
    {
      title,
      titleColor = 'white',
      titleWeight = EFontWeights.Bold,
      titleSize = '24px',
      backgroundColor = '#3c3ba8',
      backgroundImage,
      borderColor,
      borderWidth = 2,
      buttonText = 'Collect',
      products,
      onAnimationSuccessEndNoPreview,
      onClose,
      onActionBtnClicked,
      modalContainer,
      showCloseButton = true,
      price,
      isPreview = false,
    }: PostPurchaseProps,
    ref,
  ) => {
    const [open, setOpen] = useState(true);
    const [startExitAnimProduct, setStartExitAnimProduct] = useState(false);
    const [startExitAnimModal, setStartExitAnimModal] = useState(false);
    const [finalPrice, setFinalPrice] = useState('');
    const [priceBeforeDiscount, setPriceBeforeDiscount] = useState('');

    useEffect(() => {
      if (price) {
        const formattedPrice = formattingNumber(
          price.price,
          price,
          price.isCents,
          price.multiplier,
          !isPreview,
        );
        if (price.strokedPriceInCents) {
          const formattedPriceBeforeDiscount = formattingNumber(
            price?.strokedPriceInCents,
            price,
            price.isCents,
            price.multiplier,
            !isPreview,
          );
          setPriceBeforeDiscount(formattedPriceBeforeDiscount);
        }
        setFinalPrice(formattedPrice === '0' ? buttonText : formattedPrice);
      }
    }, [price]);

    const productVariantsAnimation = {
      regular: { y: 0 },
      exit: {
        y: [0, -200],
        opacity: [1, 0],
      },
    };

    const bannerVariantsAnimation = {
      enter: { scale: [0, 1.1, 1], transition: { duration: 0.4 } },
      exit: {
        scale: [1, 0],
        transition: { duration: 0.2 },
      },
    };

    const onCollectClicked = () => {
      if (isPreview) return;
      onActionBtnClicked();
    };

    const onCloseClick = () => {
      if (isPreview) return;
      setStartExitAnimModal(true);
      setTimeout(() => {
        setOpen(false);
        if (onClose) onClose({ isShowingNextProduct: false });
      }, MODAL_EXIT_TIME);
    };

    const onOrderSuccess = () => {
      if (isPreview) return;
      setStartExitAnimProduct(true);
      setTimeout(() => {
        setOpen(false);
        if (onAnimationSuccessEndNoPreview) onAnimationSuccessEndNoPreview();
      }, MODAL_EXIT_TIME);
    };

    useImperativeHandle(
      ref,
      (): { onOrderFail: () => void; onOrderSuccess: () => void } => ({
        onOrderSuccess,
        onOrderFail,
      }),
    );

    const onOrderFail = () => {
      // console.log('on order fail');
      // setStartExitAnimProduct(true);
      // onCloseClick();
    };

    return (
      <Modal
        disableEnforceFocus={isPreview}
        className={`daily-bonus-modal ${isPreview ? 'daily-bonus-modal__preview' : ''
          }`}
        open={open}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        container={modalContainer}
      >
        <motion.div
          variants={bannerVariantsAnimation}
          animate={startExitAnimModal ? 'exit' : 'enter'}
          className={'daily-bonus-border'}
          style={{
            background: borderColor
              ? createGradientOrSolid(borderColor)
              : 'var(--bundle-border-color)',
            padding: borderWidth + 'px',
          }}
        >
          <StyledDailyBonus
            titleColor={titleColor}
            titleWeight={titleWeight}
            titleSize={titleSize}
            backgroundImage={backgroundImage}
            backgroundColor={backgroundColor}
          >
            <div className="daily-bonus-banner">
              {showCloseButton && (
                <button
                  className={'daily-bonus-banner__close-icon'}
                  onClick={() => onCloseClick()}
                >
                  <ClearOutlinedIcon
                    className="closeButton"
                    style={{ color: titleColor }}
                  />
                </button>
              )}
              <h2 className="daily-bonus-banner__title">{title}</h2>
              <>
                {products && products?.length > 0 && (
                  <motion.div
                    variants={productVariantsAnimation}
                    animate={startExitAnimProduct ? 'exit' : 'regular'}
                    transition={{
                      duration: 0.3,
                      ease: 'easeInOut',
                    }}
                    className="daily-bonus-banner__product-container"
                  >
                    <PopupSingleProduct
                      quantityDisplay={products[0]?.quantityDisplay}
                      images={products[0]?.images}
                      textColor={products[0]?.textFontColorHex}
                    />
                  </motion.div>
                )}
                <motion.div
                  animate={{ scale: [0, 1, 1.05, 1] }}
                  transition={{
                    duration: 0.4,
                    delay: 0.2,
                  }}
                  style={{ width: '100%' }}
                >
                  <motion.div whileHover={{ scale: 0.9 }}>
                    <ActionButton
                      className="daily-bonus-banner__action-button"
                      text={
                        price?.price !== 0 ? (
                          <>
                            {priceBeforeDiscount &&
                              <Box className="stroked">
                                <span>{price?.currencySymbol}</span>
                                <span>{priceBeforeDiscount}</span>
                              </Box>
                            }
                            <span>{price?.currencySymbol}</span>
                            <span>{finalPrice}</span>
                          </>
                        ) : (
                          <span>{buttonText}</span>
                        )
                      }
                      action={onCollectClicked}
                      borderRadius={'7px'}
                      width={'100%'}
                      direction={
                        price?.symbolPosition === 'Right' ? 'rtl' : 'ltr'
                      }
                      marginRight={
                        price?.spacing && price.symbolPosition === 'Left'
                          ? '4px'
                          : '0px'
                      }
                      marginLeft={
                        price?.spacing && price.symbolPosition === 'Right'
                          ? '4px'
                          : '0px'
                      }
                    />
                  </motion.div>
                </motion.div>
              </>
            </div>
          </StyledDailyBonus>
        </motion.div>
      </Modal>
    );
  },
);

export default PostPurchasePopup;
